import { MARITAL_STATUSES_OPTIONS } from "@/lib/constants";
import type {
  MaritalStatus,
  Meta,
  VoucherDetailsErrors,
  VoucherDetailsFields,
} from "@/lib/types";
import { validateMeta } from "@/lib/utils";
import {
  DETAILS_QUESTIONS,
  getVisibleQuestions,
  isDetailsComplete,
  OATH_KEY,
  resolveAnswers,
} from "@/lib/vouch-flow";
import { getOtp } from "@/services";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { useSessionStorage } from "./use-session-storage";

// Stored values can contain submit-time backfills ("not-applicable") that aren't
// selectable options — drop them so they never appear as answers.
const sanitizeOption = (
  options: { value: string }[],
  value?: string,
): string =>
  options.some((option) => option.value === value) ? (value as string) : "";

export const useVoucherDetails = () => {
  const navigate = useNavigate();
  const { vouchId } = useParams<{ vouchId: string }>();

  const { getSessionStore, setSessionStore } = useSessionStorage();
  const [phone, setPhone] = useState<string>(
    () => getSessionStore<{ Phone?: string }>(vouchId!, {}).Phone ?? "",
  );
  const [fieldError, setFieldError] = useState<VoucherDetailsErrors>({});
  const [oath, setOath] = useState<boolean>(() =>
    getSessionStore<boolean>(OATH_KEY(vouchId!), false),
  );

  const handleOathChange = (checked: boolean) => {
    setOath(checked);
    setSessionStore(OATH_KEY(vouchId!), JSON.stringify(checked));
  };
  type StoredShape = Partial<Meta> & { Meta?: Partial<Meta> };

  const getBaseData = (): Partial<Meta> => {
    const stored = getSessionStore<StoredShape>(vouchId!, {});
    return stored;
  };

  const relationship = getBaseData().Relationship ?? "";

  const [meta, setMeta] = useState(() => {
    const data = getBaseData();
    return {
      Name: data.Name ?? "",
      MaritalStatus: sanitizeOption(
        MARITAL_STATUSES_OPTIONS,
        data.MaritalStatus,
      ),
    };
  });

  const clearFieldError = (field: keyof VoucherDetailsFields) =>
    setFieldError((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const updateMeta = (patch: Partial<typeof meta>) => {
    if (patch.Name !== undefined) clearFieldError("Name");
    setMeta((prev) => ({ ...prev, ...patch }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearFieldError("Phone");
    setPhone(e.target?.value);
  };

  const verifyPhoneMutation = useMutation({
    mutationFn: () => getOtp(phone.trim()),
    onSuccess: (data) => {
      if (data.ApiHttpResponse !== 202) {
        setFieldError({ Phone: data.PublicMessage });
        return;
      }

      navigate("../verify");
    },
    onError: () => {
      // Just in case of failure
      toast.error("Something went wrong, Please try again later.", {
        classNames: {
          toast: "!bg-red-50 !text-start !text-red-700",
        },
      });
    },
  });

  const handleContinue = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const baseData = getBaseData();
    if (baseData.Accuracy === undefined || baseData.Endorsement === undefined)
      return;

    const resolved = resolveAnswers(baseData.Relationship ?? "", {
      Duration: baseData.Duration,
      MaritalStatus: meta.MaritalStatus,
    });

    const incomingMeta: Meta & { Phone: string } = {
      ...(baseData as Meta),
      Phone: phone.trim(),
      Name: meta.Name,
      Duration: resolved.Duration,
      MaritalStatus: resolved.MaritalStatus as MaritalStatus,
    };

    // Moved data persistence in handleContinue from mutation because invalid Name, Phone were not persisted
    setSessionStore(vouchId!, JSON.stringify(incomingMeta));

    const errors = validateMeta({ Name: meta.Name, Phone: phone });
    if (Object.keys(errors).length > 0) {
      setFieldError(errors);
      return;
    }
    verifyPhoneMutation.mutate();
  };

  return {
    name: meta.Name,
    maritalStatus: meta.MaritalStatus,
    relationship,
    visibleQuestions: getVisibleQuestions(
      relationship,
      meta,
      DETAILS_QUESTIONS,
    ),
    phone,
    fieldError,
    setFieldError,
    oath,
    handleOathChange,
    handlePhoneChange,
    updateMeta,
    handleContinue,
    isSubmitting: verifyPhoneMutation.isPending,
    isDisabled:
      !isDetailsComplete(relationship, { ...meta, Phone: phone }) ||
      !oath ||
      verifyPhoneMutation.isPending,
  };
};
